"use client";

import {useState, type ReactNode} from "react";
import {Plus, Minus} from "lucide-react";
import {Typography} from "@repo/ui/aops-components/typography";
import {Collapse} from "@repo/ui/mantine";
import type {CoursePageSection} from "@repo/sanity-utils/query-types";
import styles from "./CourseInformation.module.css";
import {getKeyedCopyText} from "@repo/utils/keyedCopy";

type CourseInformationProps = {
	/** Course detail chips, pre-rendered by the server parent. */
	courseDetails?: ReactNode;
	/** Whether there's anything to show in the expand/collapse panel. */
	hasExpandedContent: boolean;
	/** Labeled paragraphs, textbooks, and topics, pre-rendered by the server parent. */
	children?: ReactNode;
	coursePageSection?: NonNullable<CoursePageSection>;
};

const FALLBACK_TITLE = "Course Information";
const FALLBACK_BODY =
	"Prerequisites, who should enroll, topics covered, and required materials.";
const EXPAND_KEY = "expand";
const COLLAPSE_KEY = "collapse";
const FALLBACK_EXPAND_LABEL = "Expand details";
const FALLBACK_COLLAPSE_LABEL = "Collapse details";

export function CourseInformation({
	courseDetails,
	hasExpandedContent,
	children,
	coursePageSection,
}: CourseInformationProps) {
	const [expanded, setExpanded] = useState(false);

	const title = coursePageSection?.title?.trim() || FALLBACK_TITLE;
	const body = coursePageSection?.body?.trim() || FALLBACK_BODY;
	const keyedCopy = coursePageSection?.keyedCopy ?? [];
	const expandLabel =
		getKeyedCopyText(keyedCopy, EXPAND_KEY) || FALLBACK_EXPAND_LABEL;
	const collapseLabel =
		getKeyedCopyText(keyedCopy, COLLAPSE_KEY) || FALLBACK_COLLAPSE_LABEL;

	return (
		<section className={styles.card} aria-label="Course Information">
			<div className={styles.header}>
				<Typography variant="h5" as="h2" className={styles.title}>
					{title}
				</Typography>
				<Typography variant="bodyLarge" className={styles.subtitle}>
					{body}
				</Typography>
			</div>

			{courseDetails}

			{hasExpandedContent && <div className={styles.separator} aria-hidden />}

			{hasExpandedContent && (
				<Collapse expanded={expanded} transitionDuration={200}>
					<div className={styles.expandedContent}>{children}</div>
				</Collapse>
			)}

			{hasExpandedContent && (
				<button
					type="button"
					className={styles.toggle}
					aria-expanded={expanded}
					onClick={() => setExpanded((prev) => !prev)}
				>
					<Typography variant="buttonMd" as="span">
						{expanded ? collapseLabel : expandLabel}
					</Typography>
					{expanded ? (
						<Minus aria-hidden size={20} strokeWidth={1.5} />
					) : (
						<Plus aria-hidden size={20} strokeWidth={1.5} />
					)}
				</button>
			)}
		</section>
	);
}
